import { Component } from '@theme/component';
import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { morph, MORPH_OPTIONS } from '@theme/morph';
import { yieldToMainThread, getViewParameterValue, ResizeNotifier, isMobileBreakpoint } from '@theme/utilities';

/**
 * @typedef {object} VariantPickerRefs
 * @property {HTMLFieldSetElement[]} fieldsets – The fieldset elements.
 */

const VARIANT_RESPONSE_CACHE_TTL_MS = 2 * 60 * 1000;
const MAX_VARIANT_RESPONSE_CACHE_ENTRIES = 30;
const MAX_VARIANT_PREFETCH_REQUESTS = 6;
const PRODUCT_JSON_CACHE_TTL_MS = 5 * 60 * 1000;

/** @type {Map<string, { data: any, timestamp: number }>} */
const PRODUCT_JSON_CACHE = new Map();

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [TRefs=VariantPickerRefs]
 * @extends Component<TRefs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {number | undefined} */
  #variantChangeStartedAt;

  /** @type {Map<string, { responseText: string, timestamp: number }>} */
  #responseCache = new Map();

  /** @type {Map<string, Promise<string>>} */
  #inFlightRequests = new Map();

  /** @type {string | undefined} */
  #activeRequestUrl;

  #sectionRequestId = 0;

  #onVariantChange = (event) => this.variantChanged(event);

  /** @type {Promise<any> | undefined} */
  #productDataPromise;

  /** @type {any | undefined} */
  #productData;

  /** @type {string | undefined} */
  #productDataUrl;

  /** @type {number[][]} */
  #checkedIndices = [];

  /** @type {HTMLInputElement[][]} */
  #radios = [];

  #resizeObserver = new ResizeNotifier(() => this.updateVariantPickerCss());

  connectedCallback() {
    super.connectedCallback();
    this.#radios = [];
    this.#checkedIndices = [];
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    fieldsets.forEach((fieldset) => {
      const radios = Array.from(fieldset?.querySelectorAll('input') ?? []);
      this.#radios.push(radios);

      const initialCheckedIndex = radios.findIndex((radio) => radio.dataset.currentChecked === 'true');
      if (initialCheckedIndex !== -1) {
        this.#checkedIndices.push([initialCheckedIndex]);
      }
    });

    this.addEventListener('change', this.#onVariantChange);
    this.#resizeObserver.observe(this);
    this.prefetchProductDataInIdle();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('change', this.#onVariantChange);
    this.#sectionRequestId += 1;
    this.#variantChangeStartedAt = undefined;
    this.#abortController?.abort();
    this.#inFlightRequests.clear();
    this.#responseCache.clear();
    this.#activeRequestUrl = undefined;
    this.#productDataPromise = undefined;
    this.#productData = undefined;
    this.#productDataUrl = undefined;
    this.#resizeObserver.disconnect();
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  async variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const selectedOption =
      event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;

    if (!selectedOption) return;

    this.#variantChangeStartedAt = performance.now();
    this.updateSelectedOption(event.target);
    const changedFieldsetIndex = this.getFieldsetIndex(event.target);

    let hasProductData = !!this.#productData?.variants?.length;
    if (!hasProductData && changedFieldsetIndex === 0) {
      try {
        await this.ensureProductDataLoaded();
        hasProductData = !!this.#productData?.variants?.length;
      } catch {
        hasProductData = false;
      }
    }

    if (hasProductData && changedFieldsetIndex === 0) {
      this.normalizeDependentSelections(changedFieldsetIndex);
    }

    this.updateLegendValue(event.target);
    const optimisticVariant = this.applyOptimisticVariantState(this.selectedOption ?? event.target);

    const selectedOptionAvailable = optimisticVariant
      ? optimisticVariant.available
      : selectedOption.dataset.optionAvailable === undefined
      ? undefined
      : selectedOption.dataset.optionAvailable === 'true';
    this.dispatchEvent(
      new VariantSelectedEvent({
        id: selectedOption.dataset.optionValueId ?? '',
        variantId: optimisticVariant?.id?.toString() ?? selectedOption.dataset.variantId ?? '',
        available: selectedOptionAvailable,
      })
    );

    if (!hasProductData) {
      this.ensureProductDataLoaded()
        .then(() => {
          if (!this.isConnected) return;

          if (changedFieldsetIndex === 0) {
            this.normalizeDependentSelections(changedFieldsetIndex);
          }
          this.applyOptimisticVariantState(this.selectedOption ?? event.target);
        })
        .catch(() => {});
    }

    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    if (isOnProductPage && isMobileBreakpoint()) {
      this.resetMediaGalleryToFirstSlide();
    }

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;
    const isOnFeaturedProductSection = Boolean(this.closest('featured-product-information'));

    const morphElementSelector = loadsNewProduct
      ? 'main'
      : isOnFeaturedProductSection
      ? 'featured-product-information'
      : undefined;

    const requestUrl = this.buildRequestUrl(selectedOption, null, [], {
      forceFullPageRequest: morphElementSelector === 'main',
    });
    this.fetchUpdatedSection(requestUrl, morphElementSelector);
    if (!morphElementSelector) {
      this.prefetchSiblingVariants(event.target, selectedOption);
    }

    const url = new URL(window.location.href);

    const variantId = optimisticVariant?.id?.toString() || selectedOption.dataset.variantId || null;

    if (isOnProductPage) {
      if (variantId) {
        url.searchParams.set('variant', variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    // Change the path if the option is connected to another product via combined listing.
    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      yieldToMainThread().then(() => {
        history.replaceState({}, '', url.toString());
      });
    }
  }

  resetMediaGalleryToFirstSlide() {
    const productScope = this.closest('[data-testid="product-information"], .product-information, .shopify-section');
    if (!productScope) return;

    const mediaGallery = productScope.querySelector('media-gallery');
    if (!(mediaGallery instanceof HTMLElement)) return;

    const slideshow = mediaGallery.slideshow ?? mediaGallery.querySelector('slideshow-component');
    if (!slideshow || typeof slideshow.select !== 'function') return;

    slideshow.select(0, undefined, { animate: false });
  }

  /**
   * @typedef {object} FieldsetMeasurements
   * @property {HTMLFieldSetElement} fieldset
   * @property {number | undefined} currentIndex
   * @property {number | undefined} previousIndex
   * @property {number | undefined} currentWidth
   * @property {number | undefined} previousWidth
   */

  /**
   * Gets measurements for a single fieldset (read phase).
   * @param {number} fieldsetIndex
   * @returns {FieldsetMeasurements | null}
   */
  #getFieldsetMeasurements(fieldsetIndex) {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
    const fieldset = fieldsets[fieldsetIndex];
    const checkedIndices = this.#checkedIndices[fieldsetIndex];
    const radios = this.#radios[fieldsetIndex];

    if (!radios || !checkedIndices || !fieldset) return null;

    const [currentIndex, previousIndex] = checkedIndices;

    return {
      fieldset,
      currentIndex,
      previousIndex,
      currentWidth: currentIndex !== undefined ? radios[currentIndex]?.parentElement?.offsetWidth : undefined,
      previousWidth: previousIndex !== undefined ? radios[previousIndex]?.parentElement?.offsetWidth : undefined,
    };
  }

  /**
   * Applies measurements to a fieldset (write phase).
   * @param {FieldsetMeasurements} measurements
   */
  #applyFieldsetMeasurements({ fieldset, currentWidth, previousWidth, currentIndex, previousIndex }) {
    if (currentWidth) {
      fieldset.style.setProperty('--pill-width-current', `${currentWidth}px`);
    } else if (currentIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-current');
    }

    if (previousWidth) {
      fieldset.style.setProperty('--pill-width-previous', `${previousWidth}px`);
    } else if (previousIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-previous');
    }
  }

  /**
   * Updates the fieldset CSS.
   * @param {number} fieldsetIndex - The fieldset index.
   */
  updateFieldsetCss(fieldsetIndex) {
    if (Number.isNaN(fieldsetIndex)) return;

    const measurements = this.#getFieldsetMeasurements(fieldsetIndex);
    if (measurements) {
      this.#applyFieldsetMeasurements(measurements);
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      const fieldsetIndex = Number.parseInt(target.dataset.fieldsetIndex || '');
      const inputIndex = Number.parseInt(target.dataset.inputIndex || '');

      if (!Number.isNaN(fieldsetIndex) && !Number.isNaN(inputIndex)) {
        const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
        const fieldset = fieldsets[fieldsetIndex];
        const checkedIndices = this.#checkedIndices[fieldsetIndex];
        const radios = this.#radios[fieldsetIndex];

        if (radios && checkedIndices && fieldset) {
          // Clear previous checked states
          const [currentIndex, previousIndex] = checkedIndices;

          if (currentIndex !== undefined && radios[currentIndex]) {
            radios[currentIndex].dataset.previousChecked = 'false';
          }
          if (previousIndex !== undefined && radios[previousIndex]) {
            radios[previousIndex].dataset.previousChecked = 'false';
          }

          // Update checked indices array - keep only the last 2 selections
          checkedIndices.unshift(inputIndex);
          checkedIndices.length = Math.min(checkedIndices.length, 2);

          // Update the new states
          const newCurrentIndex = checkedIndices[0]; // This is always inputIndex
          const newPreviousIndex = checkedIndices[1]; // This might be undefined

          // newCurrentIndex is guaranteed to exist since we just added it
          if (newCurrentIndex !== undefined && radios[newCurrentIndex]) {
            radios[newCurrentIndex].dataset.currentChecked = 'true';
          }

          if (newPreviousIndex !== undefined && radios[newPreviousIndex]) {
            radios[newPreviousIndex].dataset.previousChecked = 'true';
            radios[newPreviousIndex].dataset.currentChecked = 'false';
          }

          this.updateFieldsetCss(fieldsetIndex);
        }
      }
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Updates legend text values immediately on variant selection.
   * @param {Element} target - The changed input or select.
   */
  updateLegendValue(target) {
    if (!(target instanceof HTMLInputElement || target instanceof HTMLSelectElement)) return;

    const fieldset = target.closest('fieldset');
    if (!fieldset) return;

    const selectedValue = target.value;
    if (!selectedValue) return;

    const selectedValueTargets = fieldset.querySelectorAll('[data-legend-selected-value], .variant-option__swatch-value');
    selectedValueTargets.forEach((element) => {
      element.textContent = selectedValue;
    });
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @param {{ forceFullPageRequest?: boolean }} [options] - Additional URL build options.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = [], options = {}) {
    const { forceFullPageRequest = false } = options;
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];
    const viewParamValue = getViewParameterValue();

    // preserve view parameter, if it exists, for alternative product view testing
    if (viewParamValue) params.push(`view=${viewParamValue}`);

    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    const buildUrl = (baseUrl, queryParams) => {
      const query = queryParams.filter(Boolean).join('&');
      return query ? `${baseUrl}?${query}` : baseUrl;
    };

    // On the product template, request only the current section for faster variant updates.
    const isMainProductTemplateRequest =
      !forceFullPageRequest &&
      this.dataset.templateProductMatch === 'true' &&
      !!this.dataset.sectionId &&
      !this.closest('quick-add-component') &&
      !this.closest('swatches-variant-picker-component') &&
      !this.closest('featured-product-information');

    if (isMainProductTemplateRequest) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }

      return buildUrl(productUrl, [`section_id=${this.dataset.sectionId}`, ...params]);
    }

    // If variant-picker is a child of some specific sections, we need to append section_id=xxxx to the URL
    const SECTION_ID_MAP = {
      'quick-add-component': 'section-rendering-product-card',
      'swatches-variant-picker-component': 'section-rendering-product-card',
      'featured-product-information': this.closest('featured-product-information')?.id,
    };

    const closestSectionId = /** @type {keyof typeof SECTION_ID_MAP} | undefined */ (
      Object.keys(SECTION_ID_MAP).find((sectionId) => this.closest(sectionId))
    );

    if (closestSectionId) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return buildUrl(productUrl, [`section_id=${SECTION_ID_MAP[closestSectionId]}`, ...params]);
    }

    return buildUrl(productUrl, params);
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {string} [morphElementSelector] - The selector of the element to be morphed. By default, only the variant picker is morphed.
   */
  fetchUpdatedSection(requestUrl, morphElementSelector) {
    const requestStartedAt = this.#variantChangeStartedAt;
    const requestId = ++this.#sectionRequestId;

    if (this.#activeRequestUrl && this.#activeRequestUrl !== requestUrl) {
      this.#abortController?.abort();
    }

    const cachedResponse = this.#getCachedResponse(requestUrl);
    if (cachedResponse) {
      // Let the selected control paint before parsing and morphing cached HTML.
      new Promise((resolve) => requestAnimationFrame(() => setTimeout(resolve, 0)))
        .then(() => {
          this.#processUpdatedSectionResponse(requestUrl, morphElementSelector, cachedResponse, 'cache', requestStartedAt, requestId);
        })
        .catch((error) => console.error(error));
      return;
    }

    const inFlightRequest = this.#inFlightRequests.get(requestUrl);
    if (inFlightRequest) {
      inFlightRequest
        .then((responseText) => {
          this.#processUpdatedSectionResponse(requestUrl, morphElementSelector, responseText, 'dedupe', requestStartedAt, requestId);
        })
        .catch((error) => {
          if (error.name === 'AbortError') {
            console.warn('Fetch aborted by user');
          } else {
            console.error(error);
          }
        });
      return;
    }

    this.#abortController = new AbortController();
    this.#activeRequestUrl = requestUrl;

    const requestPromise = fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        this.#setCachedResponse(requestUrl, responseText);
        return responseText;
      })
      .finally(() => {
        this.#inFlightRequests.delete(requestUrl);
        if (this.#activeRequestUrl === requestUrl) {
          this.#activeRequestUrl = undefined;
        }
      });

    this.#inFlightRequests.set(requestUrl, requestPromise);

    requestPromise
      .then((responseText) => {
        this.#processUpdatedSectionResponse(requestUrl, morphElementSelector, responseText, 'network', requestStartedAt, requestId);
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          console.warn('Fetch aborted by user');
        } else {
          console.error(error);
        }
      });
  }

  /**
   * Prefetch likely sibling variant combinations (same fieldset, different value)
   * to speed up subsequent switches.
   * @param {HTMLElement} target
   * @param {HTMLElement} selectedOption
   */
  prefetchSiblingVariants(target, selectedOption) {
    if (this.dataset.templateProductMatch !== 'true') return;

    const connection = navigator.connection;
    if (connection?.saveData || connection?.effectiveType === 'slow-2g' || connection?.effectiveType === '2g') return;

    const selectedValues = this.selectedOptionsValues;
    if (!selectedValues.length) return;

    /** @type {Set<string>} */
    const prefetchUrls = new Set();

    if (target instanceof HTMLInputElement) {
      const fieldsetIndex = Number.parseInt(target.dataset.fieldsetIndex || '');
      if (Number.isNaN(fieldsetIndex)) return;

      const radios = this.#radios[fieldsetIndex] || [];
      for (const radio of radios) {
        const optionValueId = radio.dataset.optionValueId;
        if (!optionValueId || optionValueId === selectedValues[fieldsetIndex]) continue;

        const optionValues = [...selectedValues];
        optionValues[fieldsetIndex] = optionValueId;
        prefetchUrls.add(this.buildRequestUrl(selectedOption, 'product-card', optionValues));

        if (prefetchUrls.size >= MAX_VARIANT_PREFETCH_REQUESTS) break;
      }
    } else if (target instanceof HTMLSelectElement) {
      const fieldsetIndex = Number.parseInt(target.dataset.fieldsetIndex || '');
      if (Number.isNaN(fieldsetIndex)) return;

      for (const option of Array.from(target.options)) {
        const optionValueId = option.dataset.optionValueId;
        if (!optionValueId || optionValueId === selectedValues[fieldsetIndex]) continue;

        const optionValues = [...selectedValues];
        optionValues[fieldsetIndex] = optionValueId;
        prefetchUrls.add(this.buildRequestUrl(selectedOption, 'product-card', optionValues));

        if (prefetchUrls.size >= MAX_VARIANT_PREFETCH_REQUESTS) break;
      }
    }

    const urls = Array.from(prefetchUrls).filter(
      (url) => !this.#getCachedResponse(url) && !this.#inFlightRequests.has(url)
    );

    if (!urls.length) return;

    const prefetchTask = () => {
      urls.slice(0, MAX_VARIANT_PREFETCH_REQUESTS).forEach((url) => this.#prefetchRequest(url));
    };

    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(prefetchTask, { timeout: 300 });
    } else {
      setTimeout(prefetchTask, 120);
    }
  }

  prefetchProductDataInIdle() {
    if (this.dataset.templateProductMatch !== 'true') return;
    const connection = navigator.connection;
    if (connection?.saveData || connection?.effectiveType === 'slow-2g' || connection?.effectiveType === '2g') return;

    const loadProductData = () => this.ensureProductDataLoaded().catch(() => {});
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(loadProductData, { timeout: 900 });
    } else {
      setTimeout(loadProductData, 300);
    }
  }

  /**
   * @returns {Promise<any | null>}
   */
  ensureProductDataLoaded() {
    const productUrl = this.dataset.productUrl?.split('?')[0];
    if (!productUrl) return Promise.resolve(null);

    if (this.#productData && this.#productDataUrl === productUrl) {
      return Promise.resolve(this.#productData);
    }

    if (this.#productDataPromise && this.#productDataUrl === productUrl) {
      return this.#productDataPromise;
    }

    const cached = PRODUCT_JSON_CACHE.get(productUrl);
    if (cached && Date.now() - cached.timestamp <= PRODUCT_JSON_CACHE_TTL_MS) {
      this.#productDataUrl = productUrl;
      this.#productData = cached.data;
      return Promise.resolve(cached.data);
    }

    this.#productDataUrl = productUrl;
    this.#productDataPromise = fetch(`${productUrl}.js`)
      .then((response) => response.json())
      .then((productData) => {
        this.#productData = productData;
        PRODUCT_JSON_CACHE.set(productUrl, { data: productData, timestamp: Date.now() });
        return productData;
      })
      .finally(() => {
        this.#productDataPromise = undefined;
      });

    return this.#productDataPromise;
  }

  /**
   * @param {Element} target
   * @returns {any | undefined}
   */
  applyOptimisticVariantState(target) {
    if (!(target instanceof HTMLElement)) return undefined;
    if (!this.#productData?.variants?.length) return undefined;

    const selectedOptionLabels = this.selectedOptionLabels;
    if (!selectedOptionLabels.length) return undefined;

    const variant = this.findVariantBySelectedLabels(selectedOptionLabels);
    if (variant) {
      this.updatePriceFromProductData(variant);
    }

    return variant;
  }

  /**
   * @returns {string[]}
   */
  get selectedOptionLabels() {
    /** @type {Array<HTMLInputElement | HTMLOptionElement>} */
    const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));
    return selectedOptions.map((option) => option.value);
  }

  /**
   * @param {Element} target
   * @returns {number | undefined}
   */
  getFieldsetIndex(target) {
    if (target instanceof HTMLInputElement) {
      const fieldsetIndex = Number.parseInt(target.dataset.fieldsetIndex || '');
      if (!Number.isNaN(fieldsetIndex)) return fieldsetIndex;
    }

    const fieldset = target.closest('fieldset[data-fieldset-index]');
    if (!fieldset) return undefined;

    const fieldsetIndex = Number.parseInt(fieldset.dataset.fieldsetIndex || '');
    if (Number.isNaN(fieldsetIndex)) return undefined;

    return fieldsetIndex;
  }

  /**
   * @param {number} fromFieldsetIndex
   */
  normalizeDependentSelections(fromFieldsetIndex) {
    if (!this.#productData?.variants?.length) return;

    const selectedOptionLabels = this.selectedOptionLabels;
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    for (let fieldsetIndex = fromFieldsetIndex + 1; fieldsetIndex < fieldsets.length; fieldsetIndex += 1) {
      const fieldset = fieldsets[fieldsetIndex];
      if (!fieldset) continue;

      const radioInputs = Array.from(fieldset.querySelectorAll('input[data-option-value-id]'));
      if (radioInputs.length) {
        const currentInput = radioInputs.find((input) => input.checked);
        const currentValue = currentInput?.value ?? selectedOptionLabels[fieldsetIndex];

        if (
          currentValue &&
          this.isOptionValueValidForSelection(fieldsetIndex, currentValue, selectedOptionLabels, 'prefix')
        ) {
          selectedOptionLabels[fieldsetIndex] = currentValue;
          continue;
        }

        const fallbackInput = radioInputs.find((input) =>
          this.isOptionValueValidForSelection(fieldsetIndex, input.value, selectedOptionLabels, 'prefix')
        );

        if (!fallbackInput) continue;

        this.updateSelectedOption(fallbackInput);
        this.updateLegendValue(fallbackInput);
        selectedOptionLabels[fieldsetIndex] = fallbackInput.value;
        continue;
      }

      const select = fieldset.querySelector('select');
      if (!(select instanceof HTMLSelectElement)) continue;

      const currentValue = select.value;
      if (
        currentValue &&
        this.isOptionValueValidForSelection(fieldsetIndex, currentValue, selectedOptionLabels, 'prefix')
      ) {
        selectedOptionLabels[fieldsetIndex] = currentValue;
        continue;
      }

      const fallbackOption = Array.from(select.options).find((option) =>
        this.isOptionValueValidForSelection(fieldsetIndex, option.value, selectedOptionLabels, 'prefix')
      );

      if (!fallbackOption) continue;

      select.value = fallbackOption.value;
      this.updateSelectedOption(select);
      this.updateLegendValue(select);
      selectedOptionLabels[fieldsetIndex] = fallbackOption.value;
    }
  }

  /**
   * @param {number} optionIndex
   * @param {string} optionValue
   * @param {string[]} selectedOptionLabels
   * @param {'full' | 'prefix'} [matchMode='full']
   * @returns {boolean}
   */
  isOptionValueValidForSelection(optionIndex, optionValue, selectedOptionLabels, matchMode = 'full') {
    if (!this.#productData?.variants?.length) return false;

    const possibleSelection = [...selectedOptionLabels];
    possibleSelection[optionIndex] = optionValue;

    const maxIndex = matchMode === 'prefix' ? optionIndex : possibleSelection.length - 1;
    return this.hasAvailableVariantForSelection(possibleSelection, maxIndex);
  }

  /**
   * @param {string[]} selectedOptionLabels
   * @param {number} maxIndex
   * @returns {boolean}
   */
  hasAvailableVariantForSelection(selectedOptionLabels, maxIndex) {
    if (!this.#productData?.variants?.length) return false;

    return this.#productData.variants.some((variant) => {
      if (!variant.available) return false;

      for (let index = 0; index <= maxIndex; index += 1) {
        const selectedValue = selectedOptionLabels[index];
        if (!selectedValue) continue;
        if (variant[`option${index + 1}`] !== selectedValue) return false;
      }

      return true;
    });
  }

  /**
   * @param {string[]} selectedOptionLabels
   * @returns {any | undefined}
   */
  findVariantBySelectedLabels(selectedOptionLabels) {
    if (!this.#productData?.variants) return undefined;

    return this.#productData.variants.find((variant) =>
      this.variantMatchesSelectedLabels(variant, selectedOptionLabels)
    );
  }

  /**
   * @param {any} variant
   * @param {string[]} selectedOptionLabels
   * @returns {boolean}
   */
  variantMatchesSelectedLabels(variant, selectedOptionLabels) {
    return selectedOptionLabels.every((selectedValue, index) => {
      if (!selectedValue) return true;
      return variant[`option${index + 1}`] === selectedValue;
    });
  }

  /**
   * @param {string[]} selectedOptionLabels
   */
  updateOptionAvailabilityFromProductData(selectedOptionLabels) {
    const variants = this.#productData?.variants;
    if (!variants?.length) return;

    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    fieldsets.forEach((fieldset, fieldsetIndex) => {
      const optionInputs = Array.from(fieldset.querySelectorAll('input[data-option-value-id]'));
      if (!optionInputs.length) return;

      optionInputs.forEach((input) => {
        const possibleSelection = [...selectedOptionLabels];
        possibleSelection[fieldsetIndex] = input.value;

        const isAvailable = variants.some(
          (variant) => variant.available && this.variantMatchesSelectedLabels(variant, possibleSelection)
        );

        input.dataset.optionAvailable = `${isAvailable}`;
        if (isAvailable) {
          input.removeAttribute('aria-disabled');
        } else {
          input.setAttribute('aria-disabled', 'true');
        }
      });
    });
  }

  /**
   * @param {any} variant
   */
  updatePriceFromProductData(variant) {
    if (!variant) return;

    const scope = this.closest('.shopify-section, dialog, product-card') ?? document;
    const priceBlocks = this.dataset.productId
      ? Array.from(scope.querySelectorAll(`product-price[data-product-id="${this.dataset.productId}"]`))
      : Array.from(scope.querySelectorAll('product-price'));

    priceBlocks.forEach((priceBlock) => {
      const priceElement = priceBlock.querySelector('.price');
      if (priceElement) {
        priceElement.textContent = this.formatCentsUsingExistingPattern(Number(variant.price || 0), priceElement.textContent);
      }

      const compareAtPrice = Number(variant.compare_at_price || 0);
      const shouldShowCompare = compareAtPrice > Number(variant.price || 0);
      const compareElement = priceBlock.querySelector('.compare-at-price');

      if (compareElement) {
        if (shouldShowCompare) {
          compareElement.textContent = this.formatCentsUsingExistingPattern(compareAtPrice, compareElement.textContent);
          compareElement.closest('[role="group"]')?.removeAttribute('hidden');
        } else {
          compareElement.closest('[role="group"]')?.setAttribute('hidden', 'true');
        }
      }
    });
  }

  /**
   * @param {number} cents
   * @param {string | null} referenceText
   * @returns {string}
   */
  formatCentsUsingExistingPattern(cents, referenceText) {
    const locale = document.documentElement.lang || navigator.language || 'en';
    const amount = Number(cents || 0) / 100;
    const minimumFractionDigits = Number.isInteger(amount) ? 0 : 2;
    const numericAmount = new Intl.NumberFormat(locale, { minimumFractionDigits, maximumFractionDigits: 2 }).format(
      amount
    );

    if (referenceText) {
      const priceNumberPattern = /[-+]?\d[\d.,\s]*/;
      const matchedNumber = referenceText.match(priceNumberPattern)?.[0];
      if (matchedNumber) {
        const trailingWhitespace = /\s$/.test(matchedNumber) ? ' ' : '';
        return referenceText.replace(matchedNumber, `${numericAmount}${trailingWhitespace}`);
      }
    }

    const currencyCode = window.Shopify?.currency?.active;
    if (currencyCode) {
      if (currencyCode === 'EUR') return `€${numericAmount}`;
      return new Intl.NumberFormat(locale, { style: 'currency', currency: currencyCode,
        minimumFractionDigits, maximumFractionDigits: 2 }).format(amount);
    }

    return numericAmount;
  }

  /**
   * @param {string} requestUrl
   */
  #prefetchRequest(requestUrl) {
    if (this.#inFlightRequests.has(requestUrl)) return;
    if (this.#getCachedResponse(requestUrl)) return;

    const requestPromise = fetch(requestUrl)
      .then((response) => response.text())
      .then((responseText) => {
        this.#setCachedResponse(requestUrl, responseText);
        return responseText;
      })
      .finally(() => {
        this.#inFlightRequests.delete(requestUrl);
      });

    this.#inFlightRequests.set(requestUrl, requestPromise);
    requestPromise.catch(() => {});
  }

  /**
   * @param {string} requestUrl
   * @returns {string | undefined}
   */
  #getCachedResponse(requestUrl) {
    const cached = this.#responseCache.get(requestUrl);
    if (!cached) return undefined;

    if (Date.now() - cached.timestamp > VARIANT_RESPONSE_CACHE_TTL_MS) {
      this.#responseCache.delete(requestUrl);
      return undefined;
    }

    return cached.responseText;
  }

  /**
   * @param {string} requestUrl
   * @param {string} responseText
   */
  #setCachedResponse(requestUrl, responseText) {
    this.#responseCache.set(requestUrl, { responseText, timestamp: Date.now() });

    if (this.#responseCache.size <= MAX_VARIANT_RESPONSE_CACHE_ENTRIES) return;

    const oldestKey = this.#responseCache.keys().next().value;
    if (oldestKey) {
      this.#responseCache.delete(oldestKey);
    }
  }

  /**
   * @param {string} requestUrl
   * @param {string | undefined} morphElementSelector
   * @param {string} responseText
   * @param {'network' | 'cache' | 'dedupe'} source
   * @param {number | undefined} requestStartedAt
   * @param {number} requestId
   */
  #processUpdatedSectionResponse(requestUrl, morphElementSelector, responseText, source, requestStartedAt, requestId) {
    if (!this.isConnected || requestId !== this.#sectionRequestId) return;
    this.#pendingRequestUrl = undefined;
    const html = new DOMParser().parseFromString(responseText, 'text/html');
    // Defer is only useful for the initial rendering of the page. Remove it here.
    html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

    const textContent = html.querySelector(`variant-picker script[type="application/json"]`)?.textContent;
    if (!textContent) {
      if (this.#variantChangeStartedAt === requestStartedAt) {
        this.#variantChangeStartedAt = undefined;
      }
      return;
    }

    if (morphElementSelector === 'main') {
      this.updateMain(html);
    } else if (morphElementSelector) {
      this.updateElement(html, morphElementSelector);
    } else {
      const newProduct = this.updateVariantPicker(html);

      // We grab the variant object from the response and dispatch an event with it.
      if (this.selectedOptionId) {
        this.dispatchEvent(
          new VariantUpdateEvent(JSON.parse(textContent), this.selectedOptionId, {
            html,
            productId: this.dataset.productId ?? '',
            newProduct,
          })
        );
      }

      if (window.__VARIANT_PERF_DEBUG__ === true && requestStartedAt !== undefined) {
        const duration = Math.round(performance.now() - requestStartedAt);
        window.dispatchEvent(
          new CustomEvent('variant:timing', {
            detail: {
              duration,
              requestUrl,
              source,
              productId: this.dataset.productId,
            },
          })
        );
      }
    }

    if (this.#variantChangeStartedAt === requestStartedAt) {
      this.#variantChangeStartedAt = undefined;
    }
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document | Element} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
        this.#productData = undefined;
        this.#productDataPromise = undefined;
        this.#productDataUrl = undefined;
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, newVariantPickerSource, {
      ...MORPH_OPTIONS,
      getNodeKey: (node) => {
        if (!(node instanceof HTMLElement)) return undefined;
        const key = node.dataset.key;
        return key;
      },
    });
    this.updateVariantPickerCss();

    return newProduct;
  }

  updateVariantPickerCss() {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    // Batch all reads first across all fieldsets to avoid layout thrashing
    const measurements = fieldsets.map((_, index) => this.#getFieldsetMeasurements(index)).filter((m) => m !== null);

    // Batch all writes after all reads
    for (const measurement of measurements) {
      this.#applyFieldsetMeasurements(measurement);
    }
  }

  /**
   * Re-renders the desired element.
   * @param {Document} newHtml - The new HTML.
   * @param {string} elementSelector - The selector of the element to re-render.
   */
  updateElement(newHtml, elementSelector) {
    const element = this.closest(elementSelector);
    const newElement = newHtml.querySelector(elementSelector);

    if (!element || !newElement) {
      throw new Error(`No new element source found for ${elementSelector}`);
    }

    morph(element, newElement);
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain);
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset input:checked');

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error('No option value ID found');

      return optionValueId;
    });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
